<template>
  <div class="suijin-detail px-3 py-1">
    <div class="caption text--secondary">
      {{ selectedObservatory.address }}
    </div>

    <div class="caption mt-1">（m）</div>
    <div style="position: relative">
      <SuijinChart
        :height="isMobile ? 180 : 240"
        :selected-observatory="selectedObservatory"
        :chartTimeSeries="timeSeries"
        :minDate="minDate"
        :maxDate="maxDate"
        :chartWaterBoxLevel="chartWaterBoxLevel"
      />
      <v-card
        style="position: absolute; left: 160px; top: 30px;"
        class="caption py-1 px-2 font-weight-bold grey--text text--lighten-5"
        v-if="isOverMaxLevel"
        color="purple lighten-1"
      >
        {{ isJiganebori ? "冠水注意" : "冠水発生中" }}
      </v-card>
    </div>

    <v-card class="d-flex mt-1" flat>
      <div>
        <div class="caption">センサ検知高さ</div>
        <div
          class="caption d-flex"
          v-for="(sensor, i) in sensors"
          :key="sensor.id"
        >
          <v-card width="50" flat tile>
            <div class="ml-3 sensor-line" :class="`sensor${i + 1}`"></div>
          </v-card>
          <v-card width="90" flat tile>{{
            i === 0 ? "浸水検知ライン" : "浸水注意ライン"
          }}</v-card>
          <v-card width="60" flat tile class="text-right">
            {{ sensor.level + " m" }}
          </v-card>
        </div>
      </div>
      <v-spacer />
      <v-card elevation="3" width="100" v-if="hasImage" @click="dialog = true">
        <v-img :src="imageSrc" width="100" />
      </v-card>
    </v-card>

    <v-dialog
      content-class="camera-dialog"
      v-model="dialog"
      max-width="1100"
      style="z-index: 8100"
      @click="dialog = false"
    >
      <v-img :src="imageSrc" contain @click="dialog = false" />
    </v-dialog>

    <div class="mt-5 caption" v-if="['waiting', 'maintenace'].includes(status)">
      {{
        status === "maintenace" && isPrivateMode === true
          ? "この観測所はメンテナンス中です。"
          : "この観測所は待機中です。"
      }}
    </div>

    <v-simple-table
      dense
      class="mt-1"
      v-if="timeSeries.length > 0"
      fixed-header
      height="calc(100vh - 400px)"
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">日時</th>
            <th
              v-for="(sensor, i) in sensors"
              :key="sensor.id"
              class="text-center"
            >
              {{ i === 0 ? "浸水検知ライン" : "浸水注意ライン" }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(ts, index) in tableTimeSeries"
            :key="index"
            :class="{ forecat: ts.type === 'forecast' }"
            :id="`row-${index}`"
          >
            <td>{{ ts.jst.format("MM/DD HH:mm") }}</td>
            <td
              v-for="(sensor, i) in ts.sensorLevels"
              :key="i"
              class="text-center"
            >
              {{ sensor }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import SuijinChart from "./SuijinChart";
import moment from "moment";
import { isPrivateMode } from "../../../utils/common";

export default {
  name: "SuijinDetail",
  components: {
    SuijinChart
  },
  data() {
    return {
      dialog: false,
      isPrivateMode
    };
  },
  mounted() {
    setTimeout(() => {
      this.scrollNow();
    }, 500);
  },
  computed: {
    selectedObservatory() {
      return this.$store.getters.selectedObservatory;
    },
    hasImage() {
      return [
        "12217_3",
        "12217_4",
        "12217_5",
        "12217_6",
        "12217_8",
        "12217_9",
        "12217_13",
        "12217_11",
        "12217_12",
        "12217_14",
        "12217_15",
        "12217_16",
        "12217_17",
        "12218_18",
        "12219_19",
        "12220_20",
        "12221_21",
        "12222_22",
        "12223_23",
        "12224_24",
        "12225_25",
        "12226_26",
        "12227_27",
        "12228_28"
      ].includes(this.selectedObservatory.id);
    },
    isJiganebori() {
      return ["12217_11", "12217_12"].includes(this.selectedObservatory.id);
    },
    imageSrc() {
      if (this.hasImage) {
        return require(`@/assets/images/suijin/${this.selectedObservatory.id}.png`);
      } else {
        return null;
      }
    },
    sensors() {
      if (this.selectedObservatory.sensors) {
        let sensors = [...this.selectedObservatory.sensors];
        sensors.sort((a, b) => b.level - a.level);
        return sensors;
      } else {
        return [];
      }
    },
    sensorLevels() {
      return this.sensors.reduce((acc, current) => {
        acc[current.id] = current.level;
        return acc;
      }, {});
    },
    baseDate() {
      return this.$store.getters.baseDate.clone();
    },
    minDate() {
      return this.baseDate.clone().subtract(4, "hours");
    },
    maxDate() {
      return this.baseDate.clone().add(1, "hours");
    },
    timeSeries() {
      if (!this.selectedObservatory.timeSeries) {
        return [];
      }

      let targets = this.selectedObservatory.timeSeries.filter(
        ts =>
          (Object.values(ts.status).includes("05") ||
            Object.values(ts.status).includes("06") ||
            Object.values(ts.status).includes("09")) &&
          ts.status !== undefined &&
          ts.date > this.minDate.format("YYYY/MM/DD HH:mm") &&
          ts.date <= this.maxDate.format("YYYY/MM/DD HH:mm")
      );

      return targets.map(r => {
        const rowSensorLevels = { ...this.sensorLevels };
        Object.keys(rowSensorLevels).forEach(sensorId => {
          if (r.status[sensorId] === "09") {
            rowSensorLevels[sensorId] = "解除";
          } else if (["05", "06"].includes(r.status[sensorId]) === false) {
            rowSensorLevels[sensorId] = null;
          }
        });
        return {
          date: r.date,
          jst: moment.utc(r.date, "YYYY/MM/DD HH:mm").local(),
          type: r.type,
          level: r.level === null ? null : parseFloat(r.level).toFixed(3),
          sensorLevels: rowSensorLevels
        };
      });
    },
    tableTimeSeries() {
      return this.timeSeries.slice().reverse();
    },
    chartWaterBoxLevel() {
      const observatories = this.$store.getters.observatories;
      const target = observatories.find(
        obs =>
          obs.id === this.selectedObservatory.id &&
          obs.type === this.selectedObservatory.type
      );
      if (target && target.level !== null && target.level !== undefined) {
        return target.level;
      } else {
        return null;
      }
      // if (this.timeSeries.length === 0) {
      //   return null;
      // }
      // const limitDate = this.baseDate.clone().subtract(15, "minutes");
      // const levels = this.timeSeries
      //   .filter(
      //     row =>
      //       row.type === "real" &&
      //       row.date >= limitDate.format("YYYY/MM/DD HH:mm")
      //   )
      //   .map(row => parseFloat(row.level));
      // if (levels.length > 0) {
      //   return Math.max(...levels);
      // } else {
      //   return null;
      // }
    },
    isOverMaxLevel() {
      if (this.timeSeries.length === 0) {
        return false;
      }
      const sensorMaxLevel = Math.max(...this.sensors.map(s => s.level));
      return (
        this.chartWaterBoxLevel !== null &&
        this.chartWaterBoxLevel >= sensorMaxLevel
      );
    },
    status() {
      if (this.timeSeries.length > 0) {
        return "flooded";
      }
      if (
        this.selectedObservatory.timeSeries &&
        this.selectedObservatory.timeSeries.length > 0
      ) {
        return "waiting";
      }
      return "maintenace";
    },
    isMobile() {
      return this.$route.path === "/mobile";
    }
  },
  watch: {
    timeSeries() {
      setTimeout(() => {
        this.scrollNow();
      }, 500);
    }
  },
  methods: {
    scrollNow() {
      const reals = this.timeSeries.filter(row => row.type === "real");
      if (reals.length === 0) return;
      const targetIndex = reals.length - 1;
      if (targetIndex < 0) return;
      const id = `#row-${targetIndex}`;
      const height = this.$el.querySelector(id).clientHeight;
      const tableWrapper = this.$el.getElementsByClassName(
        "v-data-table__wrapper"
      )[0];
      const top = height * (targetIndex - (this.isMobile ? 3 : 5));
      tableWrapper.scroll({ top, behavior: "instant" });
    }
  }
};
</script>

<style lang="scss" scoped>
.suijin-detail {
  thead {
    background-color: #addff3;
  }
  tbody td {
    background: #eee;
  }
  tbody tr:nth-child(odd) td {
    background: #fff;
  }
  .sensor-line {
    width: 20px;
    height: 12px;
    border-bottom-width: 3px;
    border-bottom-style: dotted;
    &.sensor1 {
      border-bottom-color: #ad02ad;
    }
    &.sensor2 {
      border-bottom-color: #ffd600;
    }
  }
}
</style>
